import React, { useState } from 'react';
import styled from 'styled-components';
import { Eye, EyeOff } from 'lucide-react';

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  /* Leave room for the toggle so the text never runs under the icon */
  > input {
    width: 100%;
    padding-inline-end: 40px;
  }

  /* Hide Edge's built-in reveal button, otherwise two eyes are shown */
  > input::-ms-reveal,
  > input::-ms-clear {
    display: none;
  }
`;

const ToggleButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  top: 50%;
  inset-inline-end: 8px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: var(--border-radius-sm);
  background: transparent;
  color: ${props => (props.$visible ? 'var(--primary-color)' : 'var(--text-secondary)')};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The form's own styled input, so each field keeps its existing look */
  inputComponent?: React.ElementType;
  /** For styled inputs that mark errors with a prop instead of className */
  hasError?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  inputComponent: InputComponent = 'input',
  disabled,
  ...inputProps
}) => {
  const [visible, setVisible] = useState(false);
  const toggleLabel = visible ? 'הסתר סיסמה' : 'הצג סיסמה';

  return (
    <Wrapper>
      <InputComponent
        {...inputProps}
        disabled={disabled}
        type={visible ? 'text' : 'password'}
      />
      <ToggleButton
        type="button"
        $visible={visible}
        onClick={() => setVisible(prev => !prev)}
        disabled={disabled}
        title={toggleLabel}
        aria-label={toggleLabel}
        aria-pressed={visible}
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </ToggleButton>
    </Wrapper>
  );
};

export default PasswordInput;
